import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Transaction } from "@common/types/transaction";
import { TransactionDetailsResponseDto } from "@common/dto/transactions/transaction-details-response.dto";
import { getTransactionDetails } from "@lib/transactions";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { queryClient } from "../../../queryClient";

export const TRANSACTION_DETAILS_QUERY_KEY = "transactionDetails";

export function transactionDetailsQueryOptions(id: number) {
    return queryOptions({
        queryKey: [TRANSACTION_DETAILS_QUERY_KEY, id],
        queryFn: () => getTransactionDetails(id),
    });
}

export function useTransactionDetailsQuery(id: number | null) {
    const { data, isLoading, isError } = useQuery({
        queryKey: [TRANSACTION_DETAILS_QUERY_KEY, id],
        queryFn: () => (id ? getTransactionDetails(id) : null),
    });

    return useMemo(
        () => ({
            ...(data ?? {
                transaction: undefined,
                receiptRequired: false,
                memoRequired: false,
                origins: [],
            }),
            isLoading,
            isError,
        }),
        [data, isLoading, isError],
    );
}

export function useTransactionsByIds(ids: number[]) {
    const results = useQueries({
        queries: ids.map(transactionDetailsQueryOptions),
    });

    const isLoading = results.some((result) => result.isLoading);
    const dataStateKey = results
        .map(
            (result) =>
                `${result.dataUpdatedAt}:${result.data?.transaction.id ?? "none"}`,
        )
        .join(",");

    const transactions = useMemoByKeys(() => {
        const loadedTransactions = results
            .map((result) => result.data?.transaction)
            .filter(
                (transaction): transaction is NonNullable<typeof transaction> =>
                    transaction != null,
            );
        return loadedTransactions;
    }, [ids, dataStateKey]);

    const transactionsById = useMemo(
        () =>
            new Map(
                transactions.map((transaction) => [
                    transaction.id,
                    transaction,
                ]),
            ),
        [transactions],
    );

    return { transactions, transactionsById, isLoading };
}

export function invalidateTransactionDetailsQueries(
    transactionId?: number,
): Promise<void> {
    return queryClient.invalidateQueries({
        queryKey: transactionId
            ? [TRANSACTION_DETAILS_QUERY_KEY, transactionId]
            : [TRANSACTION_DETAILS_QUERY_KEY],
    });
}

export function mergeTransactionIntoDetailsCache(updated: Transaction) {
    // Merge-only: if no details entry exists for the id, nothing is written
    // (no seeding). Only the class assignments are replaced from the PATCH
    // response, preserving the details-panel's eager-loaded relations
    // (journalEntries, splits, etc.) that the list-shaped response omits.
    queryClient.setQueryData<TransactionDetailsResponseDto>(
        [TRANSACTION_DETAILS_QUERY_KEY, updated.id],
        (existing) =>
            existing
                ? {
                      ...existing,
                      transaction: {
                          ...existing.transaction,
                          classAssignments: updated.classAssignments,
                      },
                  }
                : existing,
    );
}

export function removeTransactionDetailsQueries(transactionId?: number) {
    queryClient.removeQueries({
        queryKey: transactionId
            ? [TRANSACTION_DETAILS_QUERY_KEY, transactionId]
            : [TRANSACTION_DETAILS_QUERY_KEY],
    });
}
